/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

// MUI
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import FlagLoader from "layouts/flagloader";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftPagination from "components/SoftPagination";
import SoftSnackbar from "components/SoftSnackbar";

// Layout (unchanged)
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Table
import DataTable from "examples/Tables/DataTable";

// Cells
import DefaultCell from "layouts/userdata/components/DefaultCell";
import StatusCell from "layouts/userdata/components/StatusCell";
import CustomerCell from "layouts/userdata/components/CustomerCell";

// Hooks – updated to new version
import { useDropdownOptions } from "./hooks/useDropdownOptions";
import UserFilters from "./components/useFilters";
import { useUsersData } from "./hooks/useUsersData";
import { getUserById } from "api/users";

// Debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

function InterviewSelected({ embedded = false }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [openingUserId, setOpeningUserId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "info",
  });

  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    status: "selected",
    order_by: "id",
    order: "desc",
  });

  // ✅ NEW – useDropdownOptions returns lazy‑loaded options and fetch functions
  const {
    options: dropdownOptions,
    loading: dropdownLoading,
    fetchBranches,
    fetchSubBranches,
    fetchShifts,
    fetchDepartments,
    fetchDesignations,
    fetchRoles,
  } = useDropdownOptions();

  const {
    users,
    loading: usersLoading,
    pagination,
    fetchUsers,
    setPagination,
  } = useUsersData();

  useEffect(() => {
    fetchUsers(
      filters,
      debouncedSearch,
      pagination.current_page,
      pagination.per_page
    );
  }, [
    filters,
    debouncedSearch,
    pagination.current_page,
    pagination.per_page,
  ]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const getApiMessage = (error, fallback) => {
    const data = error?.response?.data;

    if (data?.errors && typeof data.errors === "object") {
      return Object.values(data.errors).flat().join(" ");
    }

    return data?.message || data?.error || error?.message || fallback;
  };

  const handleOpenUser = async (user) => {
    if (!user?.id || openingUserId) return;

    setOpeningUserId(user.id);
    try {
      const response = await getUserById(user.id);
      navigate(`/user/interview-section/${user.id}`, {
        state: { preloadedUserResponse: response },
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: getApiMessage(error, "Failed to load selected user profile."),
        color: "error",
      });
    } finally {
      setOpeningUserId(null);
    }
  };

  const dataTableData = {
    columns: [
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ value, row }) => (
          <CustomerCell
            name={value}
            image={row.original.image || ""}
            color={row.original.color || "dark"}
          />
        ),
      },
      { Header: "Email", accessor: "email", Cell: DefaultCell },
      { Header: "Branch", accessor: "branch.name", Cell: DefaultCell },
      { Header: "Department", accessor: "department.name", Cell: DefaultCell },
      { Header: "Designation", accessor: "designation.name", Cell: DefaultCell },
      { Header: "Role", accessor: "role", Cell: DefaultCell },
      { Header: "Shift", accessor: "shift.type", Cell: DefaultCell },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          let icon = "hourglass_empty";
          let color = "warning";
          let status = "Pending";

          if (value === "selected") {
            icon = "done";
            color = "success";
            status = "Selected";
          } else if (value === "shortlisted") {
            icon = "star";
            color = "info";
            status = "Shortlisted";
          } else if (value === "rejected") {
            icon = "close";
            color = "error";
            status = "Rejected";
          }

          return <StatusCell icon={icon} color={color} status={status} />;
        },
      },
    ],
    rows: users,
  };

  const loading = dropdownLoading.branches || dropdownLoading.shifts || dropdownLoading.departments || dropdownLoading.designations || dropdownLoading.roles || usersLoading || Boolean(openingUserId);

  // 🔹 MAIN CONTENT (Reusable)
  const Content = (
    <SoftBox mb={embedded ? 0 : 3}>
      <Card>
        <SoftBox p={2} display="flex" flexDirection="column" gap={2}>
          {/* ✅ UPDATED FILTERS – with new props */}
          <UserFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            dropdownOptions={dropdownOptions}
            loading={dropdownLoading}
            fetchBranches={fetchBranches}
            fetchSubBranches={fetchSubBranches}
            fetchShifts={fetchShifts}
            fetchDepartments={fetchDepartments}
            fetchDesignations={fetchDesignations}
            fetchRoles={fetchRoles}
            pageContext="InterviewSelected"
          />

          {loading ? (
            <SoftBox display="flex" justifyContent="center" py={5}>
              {/* <CircularProgress color="info" /> */}
              <FlagLoader />
            </SoftBox>
          ) : users.length === 0 ? (
            <SoftBox textAlign="center" py={4}>
              <Icon sx={{ fontSize: 40, color: "text.secondary" }}>
                search_off
              </Icon>
              <SoftTypography>No records found</SoftTypography>
            </SoftBox>
          ) : (
            <DataTable
              table={dataTableData}
              entriesPerPage={{
                defaultValue: pagination.per_page,
                entries: [10, 25, 50, 100],
              }}
              canSearch={false}
              onRowClick={handleOpenUser}
            />
          )}

          <SoftPagination variant="gradient" color="info">
            {Array.from({ length: pagination.last_page }, (_, i) => (
              <SoftPagination
                key={i}
                item
                active={pagination.current_page === i + 1}
                onClick={() =>
                  setPagination((p) => ({ ...p, current_page: i + 1 }))
                }
              >
                {i + 1}
              </SoftPagination>
            ))}
          </SoftPagination>
        </SoftBox>
      </Card>
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Notification"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        time={3000}
      />
    </SoftBox>
  );

  // 🔹 CONDITIONAL LAYOUT
  if (embedded) return Content;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {Content}
      <Footer />
    </DashboardLayout>
  );
}

InterviewSelected.propTypes = {
  embedded: PropTypes.bool,
};

export default InterviewSelected;
